import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import SaveIcon from "@mui/icons-material/Save";
import { useGetBannerByIdQuery, useUpdateBannerMutation } from "../../services/banners/banners";
import { useLazyGetPreSignedUrlQuery } from "../../services/items/items";
import { uploadImage } from "../../utils/uploadImage";
import { resolveAssetUrl } from "../../utils/assets";
import { getErrorStatus, HTTP_NOT_FOUND } from "../../utils/apiError";
import { onlyNumbers, toStringSafe } from "../shared/utils";
import { BannerType } from "../../types/types";
import { BANNER_FORM_TEXTS, INPUT_LABEL_PROPS, IMAGE_BORDER_RADIUS } from "../shared/constants";
import {
  CommonBoxStyles,
  ImageUploadBar,
  SaveActionBar,
  FormFieldGrid,
  FormFieldGridResponsive,
} from "../shared/styledComponents";

type Props = {
  id: string;
};

const BannerEditForm: React.FC<Props> = ({ id }) => {
  const { data: banner, refetch, isFetching, isError } = useGetBannerByIdQuery(id, { skip: !id });

  const [getPreSignedUrl] = useLazyGetPreSignedUrlQuery();
  const [updateBanner, { isLoading: isSaving }] = useUpdateBannerMutation();

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const initialForm = useMemo(() => {
    const src = (banner as BannerType) ?? ({} as BannerType);
    return {
      title: src.title ?? "",
      alt_text: src.alt_text ?? "",
      link_url: src.link_url ?? "",
      display_order: toStringSafe(src.display_order),
      is_active: src.is_active ?? true,
      image_url: src.image_url ?? "",
    };
  }, [banner]);

  const [title, setTitle] = useState(initialForm.title);
  const [altText, setAltText] = useState(initialForm.alt_text);
  const [linkUrl, setLinkUrl] = useState(initialForm.link_url);
  const [displayOrder, setDisplayOrder] = useState(initialForm.display_order);
  const [isActive, setIsActive] = useState(initialForm.is_active);
  const [imageUrl, setImageUrl] = useState(initialForm.image_url);

  useEffect(() => {
    setTitle(initialForm.title);
    setAltText(initialForm.alt_text);
    setLinkUrl(initialForm.link_url);
    setDisplayOrder(initialForm.display_order);
    setIsActive(initialForm.is_active);
    setImageUrl(initialForm.image_url);
  }, [initialForm]);

  const imageInputRef = useRef<HTMLInputElement | null>(null);

  const openImagePicker = () => {
    setErrorMsg(null);
    imageInputRef.current?.click();
  };

  const handleImageSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setErrorMsg(null);
      const file = e.target.files?.[0];
      if (!file) return;

      setUploading(true);

      const presigned = await getPreSignedUrl({ id, _ts: Date.now() }).unwrap();
      await uploadImage(file, presigned.uploadUrl, presigned.headers["Content-Type"]);

      // The banner row stores the key, not a URL (EB-515); `Banner` resolves it at render.
      setPreviewUrl(presigned.key);
      setImageUrl(presigned.key);

      await updateBanner({ id, patch: { image_url: presigned.key } }).unwrap();
      await refetch();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Error subiendo la imagen.");
    } finally {
      setUploading(false);
      if (imageInputRef.current) imageInputRef.current.value = "";
    }
  };

  const handleSave = async (): Promise<void> => {
    setErrorMsg(null);

    const orderNumber = displayOrder ? Number(displayOrder) : 0;
    if (Number.isNaN(orderNumber)) {
      setErrorMsg(BANNER_FORM_TEXTS.numericError);
      return;
    }

    try {
      await updateBanner({
        id,
        patch: {
          title,
          alt_text: altText || title,
          link_url: linkUrl,
          display_order: orderNumber,
          is_active: isActive,
          image_url: imageUrl,
        },
      }).unwrap();
      await refetch();
    } catch (err) {
      // An edit used to upsert, so a stale id quietly created a half-built banner. It now 404s,
      // which means the row was deleted elsewhere — a different problem than a failed save.
      setErrorMsg(
        getErrorStatus(err) === HTTP_NOT_FOUND
          ? BANNER_FORM_TEXTS.notFoundError
          : BANNER_FORM_TEXTS.saveError,
      );
    }
  };

  if (isFetching && !banner) {
    return (
      <CommonBoxStyles p={2}>
        <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
          {BANNER_FORM_TEXTS.loading}
        </Typography>
      </CommonBoxStyles>
    );
  }

  if (isError || !banner) {
    return (
      <CommonBoxStyles>
        <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
          {BANNER_FORM_TEXTS.notFound}
        </Typography>
      </CommonBoxStyles>
    );
  }

  return (
    <CommonBoxStyles>
      <Box m={2} sx={{ background: "white" }}>
        <Box p={1}>
          <Typography variant="h6" fontWeight="bold" color="text.primary">
            {BANNER_FORM_TEXTS.editTitle}
          </Typography>
        </Box>
        <Box p={1}>
          <ImageUploadBar>
            <Button
              onClick={openImagePicker}
              variant="outlined"
              disabled={uploading}
              startIcon={<AddPhotoAlternateIcon />}
              fullWidth
              sx={{ fontWeight: "bold", boxShadow: 1, bgcolor: "white" }}
            >
              {uploading ? BANNER_FORM_TEXTS.uploading : BANNER_FORM_TEXTS.uploadImage}
            </Button>
          </ImageUploadBar>

          {errorMsg && (
            <Box mt={2}>
              <Alert severity="error">{errorMsg}</Alert>
            </Box>
          )}

          {(previewUrl || imageUrl) && (
            <Box mt={2}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {BANNER_FORM_TEXTS.previewLabel}
              </Typography>
              <img
                src={resolveAssetUrl(previewUrl || imageUrl)}
                alt="Vista previa"
                style={{ maxWidth: 500, width: "100%", borderRadius: IMAGE_BORDER_RADIUS }}
                referrerPolicy="no-referrer"
              />
            </Box>
          )}

          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleImageSelected}
          />

          <FormFieldGrid mt={3}>
            <TextField
              label={BANNER_FORM_TEXTS.titleLabel}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              size="small"
              fullWidth
              InputLabelProps={INPUT_LABEL_PROPS}
            />
            <TextField
              label={BANNER_FORM_TEXTS.altTextLabel}
              value={altText}
              onChange={(e) => setAltText(e.target.value)}
              size="small"
              fullWidth
              InputLabelProps={INPUT_LABEL_PROPS}
            />
            <TextField
              label={BANNER_FORM_TEXTS.linkUrlLabel}
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              size="small"
              fullWidth
              InputLabelProps={INPUT_LABEL_PROPS}
            />
          </FormFieldGrid>

          <FormFieldGridResponsive>
            <TextField
              label={BANNER_FORM_TEXTS.displayOrderLabel}
              value={displayOrder}
              onChange={(e) => setDisplayOrder(onlyNumbers(e.target.value))}
              size="small"
              fullWidth
              inputMode="numeric"
              InputLabelProps={INPUT_LABEL_PROPS}
            />
            <FormControlLabel
              control={
                <Checkbox checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              }
              label={BANNER_FORM_TEXTS.activeLabel}
            />
          </FormFieldGridResponsive>

          <SaveActionBar>
            <Button
              onClick={() => void handleSave()}
              disabled={isSaving}
              variant="outlined"
              color="success"
              startIcon={<SaveIcon />}
              fullWidth
              sx={{ fontWeight: "bold", boxShadow: 2, maxWidth: "40%", bgcolor: "white" }}
            >
              {isSaving ? BANNER_FORM_TEXTS.saving : BANNER_FORM_TEXTS.save}
            </Button>
          </SaveActionBar>
        </Box>
      </Box>
    </CommonBoxStyles>
  );
};

export default BannerEditForm;
